import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, forwardRef } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ui-radio',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiRadioComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <input
      type="radio"
      [id]="id"
      [attr.name]="name"
      [value]="value"
      [checked]="isChecked()"
      (change)="onChangeEvent($event)"
      (blur)="onBlurEvent()"
      [disabled]="disabled || disabledByForm"
      [required]="required"
      [class]="radioClass"
      [attr.aria-label]="ariaLabel"
    />
  `,
})
export class UiRadioComponent implements ControlValueAccessor {
  @Input() id?: string;
  @Input() name?: string;
  @Input() value?: string | number;
  @Input() checked = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() ariaLabel?: string;
  @Input() radioClass = 'w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500';
  @Input() formControl?: FormControl;
  @Input() ngModel?: any;

  @Output() ngModelChange = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() change = new EventEmitter<Event>();

  modelValue: any;
  disabledByForm = false;
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {
    this.modelValue = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm = isDisabled;
  }

  isChecked(): boolean {
    if (this.value !== undefined) {
      return this.modelValue == this.value;
    }
    return this.checked;
  }

  onChangeEvent(event: Event): void {
    const rawValue = (event.target as HTMLInputElement).value;
    const value = this.value ?? rawValue;
    this.modelValue = value;
    this.ngModelChange.emit(value);
    this.onChangeFn(value);
    this.valueChange.emit(String(value));
    this.change.emit(event);
  }

  onBlurEvent(): void {
    this.onTouchedFn();
  }

  onChange(event: Event): void {
    this.change.emit(event);
    this.valueChange.emit((event.target as HTMLInputElement).value);
  }
}
